import React, { useEffect, useState } from "react";
import {
  CheckCircle,
  Edit,
  Users,
  X,
  Mail,
  Search,
  UserPlus,
} from "react-feather";
import axios from "axios";
import styles from "./BookingOptions.module.css";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";

const BookingOptions = ({
  onBookingTypeSelect,
  onReturnToDashboard,
  slotData,
  refreshBookings,
}) => {
  const { user } = useAuth();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [bookingType, setBookingType] = useState(null);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [emails, setEmails] = useState("");
  const [showUserInvite, setShowUserInvite] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [customMessage, setCustomMessage] = useState(
    "Hi, I want to play this game with you!"
  );
  const [showMessageInput, setShowMessageInput] = useState(false);

  const handleBookingRequest = (type) => {
    setBookingType(type);
    if (type === "friend") {
      setShowEmailInput(true);
    } else {
      setShowConfirmation(true);
    }
  };

  const handleInviteUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/user-dashboard/get-users`,
        {
          params: { search: searchTerm },
          withCredentials: true,
        }
      );
      setUsers(response.data);
      setShowUserInvite(true);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Error fetching users. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showUserInvite) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
  }, [showUserInvite]);

  const handleUserSearch = async (searchValue) => {
    setSearchTerm(searchValue);
    if (searchValue.trim() === "") {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/user-dashboard/get-users`,
          { withCredentials: true }
        );
        setUsers(response.data);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    } else {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/api/user-dashboard/get-users`,
          {
            params: { search: searchValue },
            withCredentials: true,
          }
        );
        setUsers(response.data);
      } catch (error) {
        console.error("Error searching users:", error);
      }
    }
  };

  const handleUserSelect = (userToToggle) => {
    const currentUserId = Number(user?.uid);
    if (userToToggle.id === currentUserId) {
      toast.error("You cannot invite yourself!");
      return;
    }

    setSelectedUsers((prev) => {
      const isSelected = prev.find((u) => u.id === userToToggle.id);
      if (isSelected) {
        return prev.filter((u) => u.id !== userToToggle.id);
      } else {
        return [...prev, userToToggle];
      }
    });
  };

  const handleSendInvitations = () => {
    if (selectedUsers.length === 0) {
      toast.error("Please select at least one user to invite.");
      return;
    }
    setShowUserInvite(false);
    setShowMessageInput(true);
  };

  const handleMessageSubmit = () => {
    setShowMessageInput(false);
    setBookingType("arena_with_friends");
    setShowConfirmation(true);
  };

  const handleConfirm = async () => {
    if (bookingType === "friend" && emails.trim() === "") {
      toast.error("Please enter at least one email address.");
      return;
    }

    try {
      let bookingResponse;

      if (bookingType === "arena") {
        bookingResponse = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/api/user-dashboard/book-arena`,
          {
            slots: slotData,
          },
          { withCredentials: true }
        );
        console.log("Booking response:", bookingResponse.data);
      } else if (bookingType === "arena_with_friends") {
        bookingResponse = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/api/user-dashboard/book-arena-with-invitations`,
          {
            slots: slotData,
            invitedUsers: selectedUsers,
            message: customMessage,
          },
          { withCredentials: true }
        );
        console.log("Booking with invitations response:", bookingResponse.data);
      }

      if (bookingResponse && bookingResponse.status === 200) {
        toast.success(bookingResponse.data.message || "Arena booked successfully!");
        setShowConfirmation(false);
        setShowEmailInput(false);
        setShowMessageInput(false);
        setEmails("");
        setSelectedUsers([]);
        setCustomMessage("Hi, I want to play this game with you!");

        if (refreshBookings) {
          await refreshBookings();
        }

        onBookingTypeSelect(
          bookingType,
          bookingType === "friend"
            ? emails.split(",").map((email) => email.trim())
            : []
        );
        onReturnToDashboard();
      }
    } catch (error) {
      console.error("Error booking arena:", error);
      const errorMessage =
        error.response?.data?.error ||
        "There was an error booking the arena. Please try again.";
      toast.error(errorMessage);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    setShowEmailInput(false);
    setShowConfirmation(true);
  };

  return (
    <div className={styles.bookingOptions}>
      <h3>What Would You Like to Do?</h3>
      <div className={styles.bookButtons}>
        <button
          className={`${styles.bookingButton} ${styles.requestBtn}`}
          onClick={() => handleBookingRequest("arena")}
        >
          <CheckCircle size={16} className={styles.icon} />
          Book Arena
        </button>
        <button
          className={`${styles.bookingButton} ${styles.inviteButton}`}
          onClick={handleInviteUsers}
          disabled={loading}
        >
          <UserPlus size={16} className={styles.icon} />
          {loading ? "Loading..." : "Invite Friends"}
        </button>
      </div>

      {showUserInvite && (
        <div className={`${styles.modal} `}>
          <div className={styles.modalContent}>
            <button
              className={styles.closeButton}
              onClick={() => {
                setShowUserInvite(false);
                setSelectedUsers([]);
                setSearchTerm("");
              }}
            >
              <X size={24} />
            </button>
            <h3 className={styles.modalTitle}>Invite Friends to Play</h3>
            <div className={styles.searchWrapper}>
              <Search size={16} className={styles.searchIcon} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleUserSearch(e.target.value)}
                placeholder="Search users by first name..."
                className={styles.searchInput}
              />
            </div>
            <div className={styles.usersList}>
              {users.map((user) => {
                const isSelected = selectedUsers.find((u) => u.id === user.id);
                const currentUserId = Number(user?.uid);
                const isCurrentUser = user.id === currentUserId;
                return (
                  <div
                    key={user.id}
                    className={`${styles.userItem} ${
                      isSelected ? styles.selected : ""
                    } ${isCurrentUser ? styles.disabled : ""}`}
                    onClick={() => !isCurrentUser && handleUserSelect(user)}
                  >
                    <div className={styles.userInfo}>
                      <strong>
                        {user.first_name} {user.last_name}
                      </strong>
                      <span className={styles.userEmail}>{user.email}</span>
                      {isCurrentUser && (
                        <span className={styles.currentUserLabel}>(You)</span>
                      )}
                    </div>
                    {isSelected && !isCurrentUser && (
                      <CheckCircle size={20} className={styles.selectedIcon} />
                    )}
                  </div>
                );
              })}
            </div>
            {/* {selectedUsers.length > 0 && (
              <div className={styles.selectedUsersSummary}>
                <div className={styles.selectedUsersList}>
                  {selectedUsers.map((user) => (
                    <span key={user.id} className={styles.selectedUserTag}>
                      {user.first_name} {user.last_name}
                      <X
                        size={14}
                        onClick={() => handleUserSelect(user)}
                        className={styles.removeUser}
                      />
                    </span>
                  ))}
                </div>
              </div>
            )} */}
            <div className={styles.menuButtons}>
              <button
                className={styles.sendInvitationsButton}
                onClick={handleSendInvitations}
                disabled={selectedUsers.length === 0}
              >
                <Edit size={14} /> Write a Message
              </button>
              <button
                className={styles.sendInvitationsButton}
                onClick={handleMessageSubmit}
                disabled={selectedUsers.length === 0}
              >
                <CheckCircle size={14} /> Book Arena
              </button>
            </div>
          </div>
        </div>
      )}

      {showMessageInput && (
        <div className={`${styles.modal}`}>
          <div className={styles.modalContent}>
            <button
              className={styles.closeButton}
              onClick={() => {
                setShowMessageInput(false);
                setSelectedUsers([]);
              }}
            >
              <X size={24} />
            </button>
            <h3 className={styles.modalTitle}>Add a Message</h3>
            <p className={styles.modalSubtitle}>
              Write a message to send with your invitation:
            </p>
            <div className={styles.messageInputWrapper}>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Hi, I want to play this game with you!"
                className={styles.messageTextarea}
                rows={4}
                maxLength={500}
              />
              <div className={styles.characterCount}>
                {customMessage.length}/500 characters
              </div>
            </div>
            <div className={styles.messageActions}>
              <button
                className={styles.useDefaultButton}
                onClick={() => {
                  setCustomMessage("Hi, I want to play this game with you!");
                  handleMessageSubmit();
                }}
              >
                Use Default Message
              </button>
              <button
                className={styles.sendCustomButton}
                onClick={handleMessageSubmit}
              >
                Send with Custom Message
              </button>
            </div>
          </div>
        </div>
      )}

      {showEmailInput && (
        <div className={`${styles.modal} ${styles.animateFadeIn}`}>
          <div className={styles.modalContent}>
            <button
              className={styles.closeButton}
              onClick={() => setShowEmailInput(false)}
            >
              <X size={24} />
            </button>
            <h3 className={styles.modalTitle}>
              Enter Friend's Email Addresses
            </h3>
            <div className={styles.emailInputWrapper}>
              <Mail size={16} className={styles.inputIcon} />
              <input
                type="text"
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                placeholder="Enter emails separated by commas"
                className={styles.emailInput}
              />
            </div>
            <button className={styles.submitButton} onClick={handleEmailSubmit}>
              Submit
            </button>
          </div>
        </div>
      )}

      {showConfirmation && (
        <div className={`${styles.modal}`}>
          <div className={styles.modalContent}>
            <button
              className={styles.closeButton}
              onClick={() => setShowConfirmation(false)}
            >
              <X size={24} />
            </button>
            <h3 className={styles.modalTitle}>Booking Confirmation</h3>
            <p className={styles.modalSubtitle}>
              {bookingType === "arena_with_friends"
                ? `Your booking application has been sent and invitations sent to ${selectedUsers.length} user(s).`
                : "Your booking application has been sent."}
            </p>
            <button className={styles.okButton} onClick={handleConfirm}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingOptions;
